import { EllipsisVerticalIcon, LogOutIcon, WalletIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { config } from '@/config'
import { useWeb3Context } from '@/contexts/Web3Provider'
import { IconNames } from '@/enums'
import { abbrCenter } from '@/helpers/formatters'
import { RoutePaths } from '@/routes'
import { cn } from '@/theme/utils'
import { UiButton } from '@/ui/UiButton'
import { UiButtonGradient } from '@/ui/UiButtonGradient'
import UiContainer from '@/ui/UiContainer'
import {
  UiDropdownMenu,
  UiDropdownMenuContent,
  UiDropdownMenuItem,
  UiDropdownMenuSeparator,
  UiDropdownMenuTrigger,
} from '@/ui/UiDropdownMenu'
import UiIcon from '@/ui/UiIcon'

import Logo from './Logo'

export default function Navbar() {
  const location = useLocation()

  const { address, isConnected, connect, disconnect } = useWeb3Context()

  const AccountComponent = useMemo(() => {
    if (!isConnected) {
      return (
        <UiButtonGradient variant='outline' size='md' onClick={connect}>
          Connect Wallet
        </UiButtonGradient>
      )
    }

    return (
      <div className='border-border flex items-center gap-2 rounded-full border-1 px-2 py-1'>
        <WalletIcon className='text-foreground ml-1 size-5' />
        <span className='text-foreground typography-m3-body-large'>
          {abbrCenter(address)}
        </span>

        <UiButtonGradient size='icon' variant='secondary' onClick={disconnect}>
          <LogOutIcon className='size-4 text-[#F5D7AA]' />
        </UiButtonGradient>
      </div>
    )
  }, [address, connect, disconnect, isConnected])

  const navLinks = [
    { to: RoutePaths.Root, iconName: IconNames.Swap, label: 'Swap' },
    {
      to: RoutePaths.LiquidityPoolsList,
      iconName: IconNames.Waterdrop,
      label: 'Pools',
    },
    {
      to: config.DOC_LINK,
      iconName: IconNames.Library,
      label: 'Docs',
    },
    {
      to: config.FAUCET_LINK,
      iconName: IconNames.Coin,
      label: 'Get $BRIDGE',
    },
    {
      to: RoutePaths.Explore,
      iconName: IconNames.Search,
      label: 'Explore',
    },
  ]

  return (
    <UiContainer className='flex items-center justify-between gap-4 py-4'>
      <div className='flex flex-1 items-center md:items-end'>
        <Logo className='mr-6 -translate-y-1.5' />

        <div className='hidden w-full flex-1 items-center justify-center gap-4 md:ml-0 lg:flex'>
          {navLinks.map((el, idx) => (
            <Link
              key={idx}
              to={el.to}
              className={cn(
                'rounded-full bg-[#0D0E1266] px-4 py-2',
                'text-foreground typography-m3-body-large flex items-center gap-2 font-bold',
                location.pathname === el.to && 'bg-[#0D0E12]',
              )}
            >
              <UiIcon name={el.iconName} className='size-5' />
              <span className='gradient-text'>{el.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <UiDropdownMenu>
        <UiDropdownMenuTrigger className='lg:hidden' asChild>
          <UiButton variant='ghost' size='icon'>
            <EllipsisVerticalIcon className='text-foreground size-4' />
          </UiButton>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent>
          {navLinks.map((el, idx) => (
            <UiDropdownMenuItem>
              <Link
                key={idx}
                to={el.to}
                className={cn(
                  'w-full rounded-full px-4 py-2',
                  'text-foreground typography-m3-body-large flex items-center gap-2 font-bold',
                )}
              >
                <UiIcon name={el.iconName} className='size-5' />
                <span className='gradient-text'>{el.label}</span>
              </Link>
            </UiDropdownMenuItem>
          ))}
          <UiDropdownMenuSeparator />
          <UiDropdownMenuItem>
            <div className='flex w-full justify-center'>{AccountComponent}</div>
          </UiDropdownMenuItem>
        </UiDropdownMenuContent>
      </UiDropdownMenu>

      <div className='hidden gap-4 lg:flex'>{AccountComponent}</div>
    </UiContainer>
  )
}
